import React from "react";
import SearchIcon from "@mui/icons-material/Search";
import { Autocomplete, AutocompleteRenderInputParams, InputAdornment, TextField, Typography } from "@mui/material";
import { BasicTopicGroup } from "modules/topics/models";

interface ComponentProps {
    topicGroups: BasicTopicGroup[];
    onSelect: (groupId?: string) => void;
    defaultGroup?: BasicTopicGroup;
    errorText?: string;
}

const TopicGroupSearch: React.FunctionComponent<ComponentProps> = (props) => {
    const LIST_ITEM_HEIGHT = 36;
    const ITEMS_VISIBLE = 5;

    const [textToSearch, setTextToSearch] = React.useState("");
    const [value, setValue] = React.useState<BasicTopicGroup | null>(props?.defaultGroup || null);

    const boldedLabel = (text: string, firstCut: number) : JSX.Element => {
        const secondCut = firstCut + textToSearch.length;

        return <React.Fragment>
            <Typography>
                {text.slice(0, firstCut)}
            </Typography>

            <Typography fontWeight={500}>
                {text.slice(firstCut, secondCut)}
            </Typography>

            <Typography style={{ whiteSpace: "pre-wrap" }}>
                {text.slice(secondCut)}
            </Typography>
        </React.Fragment>;
    };

    const getBoldedLabel = (groupName: string) : JSX.Element | string => {
        const textToLower = textToSearch.toLowerCase().trim();
        const firstCut = groupName.toLowerCase().indexOf(textToLower);

        return firstCut < 0 ? groupName : boldedLabel(groupName, firstCut);
    };

    const noMatchesText = () : JSX.Element => <i>No matches found</i>;

    const searchIcon = (icon : React.ReactNode) : JSX.Element => {
        return <React.Fragment>
            <InputAdornment position="start">
                <SearchIcon />
            </InputAdornment>
            {icon}
        </React.Fragment>;
    };

    const renderTextInput = (params : AutocompleteRenderInputParams) : JSX.Element => {
        return <TextField {...params}
            placeholder="Search and select topic group"
            helperText={props.errorText}
            error={!!props.errorText}
            InputProps={{
                ...params.InputProps,
                startAdornment: searchIcon(params.InputProps.startAdornment),
                style: { color: !!props.errorText ? "red" : "canvastext" }
            }}
        />;
    };

    const handleChange = (value: BasicTopicGroup | null) => {
        setValue(value);
        props.onSelect(value?.id);
    };

    return (
        <Autocomplete
            fullWidth
            disablePortal
            selectOnFocus
            clearOnBlur
            forcePopupIcon={false}
            noOptionsText={noMatchesText()}
            options={props.topicGroups}
            value={value || null}
            onChange={(_, value) => handleChange(value)}
            onInputChange={(_, val) => setTextToSearch(val)}
            ListboxProps={{ style: { maxHeight: LIST_ITEM_HEIGHT * ITEMS_VISIBLE } }}
            isOptionEqualToValue={(option, value) => option.id === value.id || value.id === ""}
            renderInput={(params : AutocompleteRenderInputParams) => renderTextInput(params)}
            renderOption={(props, option: BasicTopicGroup) => (
                <li {...props}>
                    {textToSearch.trim().length === 0 ? option.label : getBoldedLabel(option.label)}
                </li>
            )}  
        />
    );
};

export default TopicGroupSearch;